import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n.js';
import { api } from '../lib/api.js';

const LANG_LABELS: Record<SupportedLanguage, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  ja: '日本語',
  'pt-BR': 'Português (Brasil)',
  'zh-CN': '中文（简体）',
};

const STORAGE_KEY = 'acs_language_set';

export function hasPickedLanguage(): boolean {
  return localStorage.getItem(STORAGE_KEY) === '1';
}

export function LanguagePicker({ onDone }: { onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [lang, setLang] = useState<SupportedLanguage>(
    (i18n.language as SupportedLanguage) || 'en',
  );
  const [saving, setSaving] = useState(false);
  const [hover, setHover] = useState(false);

  const handleContinue = async () => {
    setSaving(true);
    try {
      await api.put('/api/settings', { language: lang });
      void i18n.changeLanguage(lang);
      localStorage.setItem(STORAGE_KEY, '1');
      onDone();
    } catch {
      // Still continue even if save fails — language will use browser detection
      localStorage.setItem(STORAGE_KEY, '1');
      onDone();
    }
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', padding: 16,
    }}>
      <div style={{
        background: 'var(--p-color-bg-surface, #fff)',
        border: '1px solid var(--p-color-border, #E3E3E3)',
        borderRadius: 12, padding: '40px 32px', maxWidth: 480, width: '100%',
        textAlign: 'center',
      }}>
        <h2 style={{
          fontSize: 18, fontWeight: 600, marginBottom: 8,
          color: 'var(--p-color-text, #303030)',
        }}>
          {t('languagePicker.title')}
        </h2>
        <p style={{
          fontSize: 13, color: 'var(--p-color-text-subdued, #6D7175)',
          marginBottom: 24, lineHeight: 1.5,
        }}>
          {t('languagePicker.subtitle')}
        </p>

        <select
          value={lang}
          onChange={(e) => setLang(e.target.value as SupportedLanguage)}
          style={{
            width: '100%', padding: '10px 14px',
            border: '1px solid var(--p-color-border, #C9CCCF)',
            borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
            background: 'var(--p-color-bg-surface, #fff)',
            color: 'var(--p-color-text, #303030)',
            marginBottom: 16, appearance: 'auto',
          }}
        >
          {SUPPORTED_LANGUAGES.map((code) => (
            <option key={code} value={code}>{LANG_LABELS[code]}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={handleContinue}
          disabled={saving}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            width: '100%', padding: '10px 24px',
            background: hover && !saving ? '#004BB0' : '#005BD3',
            color: '#fff', border: 'none', borderRadius: 8,
            fontSize: 14, fontWeight: 600, cursor: saving ? 'default' : 'pointer',
            fontFamily: 'inherit', opacity: saving ? 0.7 : 1,
            transition: 'background 0.15s',
          }}
        >
          {saving ? '…' : t('languagePicker.continue')}
        </button>

        <p style={{
          fontSize: 12, color: 'var(--p-color-text-subdued, #8C8C8C)',
          marginTop: 14,
        }}>
          {t('languagePicker.hint')}
        </p>
      </div>
    </div>
  );
}
